// @greenlight-spec: 28-it-dashboard-ui.md
import { Navigate, Route, Routes, useParams, useSearchParams } from 'react-router-dom';

import { AppShell } from '@/components/app-shell/AppShell';
import { InstallLayout } from '@/components/app-shell/InstallLayout';
import { InstallCompleteGuard } from '@/components/install/InstallCompleteGuard';
import { InstallGuard } from '@/components/install/InstallGuard';
import { AppsPage } from '@/pages/AppsPage';
import { AppDetailPage } from '@/pages/AppDetailPage';
import { AuditPage } from '@/pages/AuditPage';
import { CompliancePage } from '@/pages/CompliancePage';
import { CostPage } from '@/pages/CostPage';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { InternalHealthPage } from '@/pages/InternalHealthPage';
import { KnowledgePage } from '@/pages/KnowledgePage';
import { LoginPage } from '@/pages/LoginPage';
import { MarketplacePage } from '@/pages/MarketplacePage';
import { CliApprove } from '@/pages/CliApprove';
import { CliSignedIn } from '@/pages/CliSignedIn';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { UsersPage } from '@/pages/UsersPage';
import { AgentSetupPage } from '@/pages/AgentSetupPage';
import { AgentsPage } from '@/pages/agents/AgentsPage';
import { AgentDetailPage } from '@/pages/agents/AgentDetailPage';
import { SessionPage } from '@/pages/agents/SessionPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { InstallPage, NotFoundPage } from '@/pages/placeholder';
import { AccessDeniedPage } from '@/pages/AccessDeniedPage';
import { ConnectPage } from '@/pages/ConnectPage';
import { ConnectResultPage } from '@/pages/ConnectResultPage';

/**
 * The per-app permission view lives in the Access tab inside the app-detail
 * page; this legacy path redirects there so a single surface owns per-app grants.
 */
function AppPermissionsRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={`/apps/${id}?tab=access`} replace />;
}

/**
 * The cross-app grants inventory merged into the unified Integrations tab as its
 * Grants sub-view (docs/28 §/integrations). This legacy path redirects there,
 * preserving any inbound filter params (e.g. group/appId from a deep-link).
 */
function PermissionsRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set('view', 'grants');
  return <Navigate to={`/integrations?${next.toString()}`} replace />;
}

export function GreenlightRoutes() {
  return (
    <Routes>
      <Route element={<InstallCompleteGuard />}>
        <Route path="/install" element={<InstallLayout />}>
          <Route index element={<InstallPage />} />
        </Route>
      </Route>

      {/* Unauthenticated sign-in surface (docs/10 §Login flow). Outside the
          InstallGuard and AppShell: it must render without a session and never
          redirect on load (the loop-avoidance contract with /auth/login). */}
      <Route path="/login" element={<LoginPage />} />

      {/* CLI sign-in success page (ADR-0028). Public, outside the InstallGuard
          and AppShell: the `greenlight login` loopback redirects the browser
          here, where there is no dashboard session yet. */}
      <Route path="/cli/done" element={<CliSignedIn />} />

      {/* The page every connect_url opens (docs/48 §Per-user OAuth, ADR-0086):
          one Connect button, and only that click reaches the provider. Outside
          the AppShell: the person is mid-task in an app. */}
      <Route path="/connect" element={<ConnectPage />} />

      {/* Where /connect/callback sends a browser whose per-user OAuth connect did
          not end in a stored connection (docs/48 §Per-user OAuth). Outside the
          AppShell: the person is mid-redirect into an app. */}
      <Route path="/connect/result" element={<ConnectResultPage />} />

      {/* Browser approval for a CLI sign-in (ADR-0028), for when the agent has no
          working MCP connection. Outside the AppShell — one focused task, often
          for someone who never opens the dashboard — but it needs a session, so
          it sends a signed-out visitor through SSO with a code-free return_to.
          The URL the CLI prints carries no code; it is typed into the form. */}
      <Route path="/cli/approve" element={<CliApprove />} />

      <Route element={<InstallGuard />}>
        <Route element={<AppShell />}>
          <Route index element={<Navigate to="/apps" replace />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/apps/:id" element={<AppDetailPage />} />
          {/* Run detail keeps its own URL (alert deep-links) but renders as a
              slide-over layered on the app detail page (docs/28). */}
          <Route path="/apps/:id/runs/:runId" element={<AppDetailPage />} />
          <Route path="/apps/:id/permissions" element={<AppPermissionsRedirect />} />
          <Route path="/permissions" element={<PermissionsRedirect />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/internal-health" element={<InternalHealthPage />} />
          {/* Self-gates on the cost-management preview flag and org_admin. */}
          <Route path="/cost" element={<CostPage />} />
          {/* Self-gate on the greenlight-agents preview flag; fixture data only. */}
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/agents/:agentId" element={<AgentDetailPage />} />
          <Route path="/agents/:agentId/sessions/:sessionId" element={<SessionPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/agent-setup" element={<AgentSetupPage />} />
          {/* Where /auth/check sends a refused browser navigation. Must sit
              before the catch-all, which would otherwise call a real refusal a
              missing page. */}
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
